// "Create company" on /welcome (NoCompanyView.create + SyncCenter.createCompany, WEB_PLAN.md §5.2):
// 1. the seed is built first (a company this build couldn't seed is never created) and stored with the profile id
//    before `create_company` is called, so a create whose answer never arrives, or a tab closed meanwhile, can still be
//    found by that id and seeded (iOS keeps `pendingCreateProfileId` for the same reason);
// 2. `create_company` with that profile id (reused for a retry while the outcome is unknown, so a create that went
//    through is found instead of failing with already_member);
// 3. the seed pushed as one batch; "Finishing setup…" only once a try failed;
// 4. `onReady` once everything is on the server: the page then re-reads memberships and the console opens.

import type { User } from '@supabase/supabase-js';
import { useCallback, useRef, useState } from 'react';
import { accountLabel } from '@/auth/accountText';
import { isSessionFailure } from '@/auth/errors';
import { newId } from '@/contracts/uuid';
import type { PushItem } from '@/contracts/wire';
import { useLatest } from '@/ui/hooks';
import { createCompany } from './api';
import {
  companySeedItems,
  outcomeUnknown,
  type CompanyPendingSetup,
  type PendingSetup,
  type SetupProblem,
} from './companySetup';
import { clearPendingSetup, loadPendingSetup, pushSeed, savePendingSetup, seedEnvelope } from './setupRunner';
import { createErrorMessage, SEED_BUILD_FAILED_TEXT } from './welcomeText';

export type SetupState =
  | { phase: 'idle' }
  | { phase: 'creating' }
  | { phase: 'finishing'; companyName: string; problem: SetupProblem | null; retrying: boolean }
  | { phase: 'ready'; companyName: string };

export interface CreateInput {
  /** Already cleaned (cleanCompanyName). */
  companyName: string;
  firstName: string;
  lastName: string;
}

export interface CompanySetup {
  state: SetupState;
  /** Why the last create failed (shown under the button); null otherwise. */
  error: string | null;
  create: (input: CreateInput) => Promise<void>;
  /** "Try again" after the automatic tries stopped. */
  retry: () => void;
}

export function useCompanySetup(user: User | null, onReady: (companyName: string) => void): CompanySetup {
  const [state, setState] = useState<SetupState>({ phase: 'idle' });
  const [error, setError] = useState<string | null>(null);
  /** The profile id of a create whose answer never arrived: a retry sends the same one (also kept in storage). */
  const unknownProfileId = useRef<string | null>(null);
  /** The setup still to finish (what the last run left). */
  const setup = useRef<CompanyPendingSetup | null>(null);
  const busy = useRef(false);
  const onReadyRef = useLatest(onReady);

  const run = useCallback(async (s: CompanyPendingSetup, firstTry: boolean) => {
    setup.current = s;
    if (!firstTry) setState({ phase: 'finishing', companyName: s.companyName, problem: null, retrying: true });
    const result = await pushSeed(s, {
      onRetrying: (problem) => setState({ phase: 'finishing', companyName: s.companyName, problem, retrying: true }),
    });
    if (result.done) {
      setup.current = null;
      setState({ phase: 'ready', companyName: s.companyName });
      onReadyRef.current(s.companyName);
      return;
    }
    setup.current = { ...s, items: result.remaining };
    setState({ phase: 'finishing', companyName: s.companyName, problem: result.problem, retrying: false });
  }, [onReadyRef]);

  const create = useCallback(async (input: CreateInput) => {
    if (!user || busy.current) return;
    busy.current = true;
    setError(null);
    setState({ phase: 'creating' });
    try {
      // A create whose answer never arrived (in this page, or before a reload) is retried with the same profile id.
      const stored = loadPendingSetup(user.id);
      const profileId = unknownProfileId.current ?? (stored?.companyId === null ? stored.profileId : null) ?? newId();
      const build = (companyName: string): PushItem[] => companySeedItems(
        {
          companyName,
          profileId,
          firstName: input.firstName,
          lastName: input.lastName,
          email: user.email?.trim() ?? '',
          // PhoneNumber.display of the account's number, as iOS fills the admin profile.
          phone: user.phone ? (accountLabel({ phone: user.phone }) ?? '') : '',
          locale: typeof navigator === 'undefined' ? null : navigator.language,
          now: new Date(),
        },
        seedEnvelope(),
      );

      let items: PushItem[];
      try {
        items = build(input.companyName);
      } catch (e) {
        // A seed this build can't assemble is a bug: say so, and create nothing (a company without settings would
        // send iPhones to setup).
        console.error('Building the company seed failed', e);
        setState({ phase: 'idle' });
        setError(SEED_BUILD_FAILED_TEXT);
        return;
      }
      const pending: PendingSetup = { version: 1, authUserId: user.id, profileId, companyId: null, companyName: input.companyName, items };
      savePendingSetup(pending);

      let companyId: string;
      let companyName: string;
      try {
        const m = await createCompany(input.companyName, profileId);
        companyId = m.company_id.toLowerCase();
        companyName = m.company_name?.trim() || input.companyName;
      } catch (e) {
        if (outcomeUnknown(e)) {
          unknownProfileId.current = profileId;
        } else {
          // A definite refusal: nothing was created, so the next try starts over with a new profile.
          unknownProfileId.current = null;
          clearPendingSetup(user.id);
        }
        setState({ phase: 'idle' });
        if (!isSessionFailure(e)) setError(createErrorMessage(e));
        return;
      }
      unknownProfileId.current = null;

      // The settings carry the name the server kept (create_company cleans it the way cleanCompanyName does, so this
      // rebuild is for a server whose rule changed). Nothing was pushed yet, so new default ids are fine.
      if (companyName !== input.companyName) {
        try {
          items = build(companyName);
        } catch (e) {
          console.error('Rebuilding the company seed failed; keeping the typed name', e);
          companyName = input.companyName;
        }
      }
      const ready: CompanyPendingSetup = { ...pending, companyId, companyName, items };
      savePendingSetup(ready);
      await run(ready, true);
    } finally {
      busy.current = false;
    }
  }, [run, user]);

  const retry = useCallback(() => {
    const s = setup.current;
    if (!s || busy.current) return;
    busy.current = true;
    void run(s, false).finally(() => {
      busy.current = false;
    });
  }, [run]);

  return { state, error, create, retry };
}
